import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useInstructorMode } from '../hooks/useInstructorMode';
import { formatShortDate } from '../utils/dateUtils';
import './TopArtists.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function TopArtists() {
  const { buildUrl, isInstructorMode } = useInstructorMode();
  
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const limit = 250;

  const fetchArtists = useCallback(async () => {
    try {
      setLoading(true);
      
      let url = `${API_BASE}/artists/top?limit=${limit}`;
      if (isInstructorMode) {
        url += '&show=all';
      }
      
      const response = await axios.get(url);
      setArtists(response.data.artists);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load artists');
      console.error('Error fetching top artists:', err);
    } finally {
      setLoading(false);
    }
  }, [limit, isInstructorMode]);

  useEffect(() => {
    fetchArtists();
  }, [fetchArtists]);

  if (error) {
    return (
      <div className="top-artists error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="top-artists">
      <div className="top-artists-header">
        <h1>🎤 Top Artists</h1>
        <p className="subtitle">Top {limit} artists from your cycling classes</p>
      </div>

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading artists...</p>
        </div>
      )}

      {!loading && artists.length === 0 && (
        <div className="no-results">
          <p>No artists found.</p>
        </div>
      )}

      {!loading && artists.length > 0 && (
        <div className="artists-table-container">
          <table className="artists-table">
            <thead>
              <tr>
                <th className="col-rank">#</th>
                <th className="col-artist">ARTIST</th>
                <th className="col-songs">SONGS</th>
                <th className="col-plays">PLAYS</th>
                <th className="col-last">LAST PLAYED</th>
              </tr>
            </thead>
            <tbody>
              {artists.map((artist, index) => (
                <tr key={artist.id}>
                  <td className="col-rank">{index + 1}</td>
                  <td className="col-artist">
                    <a 
                      href={buildUrl(`/artist/${artist.id}`)}
                      className="artist-link"
                    >
                      {artist.name}
                    </a>
                  </td>
                  <td className="col-songs">
                    {artist.total_songs}
                  </td>
                  <td className="col-plays">
                    {artist.total_plays}
                  </td>
                  <td className="col-last">
                    {artist.last_played_date ? formatShortDate(artist.last_played_date) : 'Never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="top-artists-actions">
        <a href={buildUrl('/')} className="btn-back">← Back to All Playlists</a>
      </div>
    </div>
  );
}

export default TopArtists;